"use client"

import { usePathname } from "next/navigation"
import { useRef } from "react"
import { usePageView } from "use-page-view"
import { PA_PAGE_VIEWS_ENDPOINT, PA_HEADERS } from "./config"
import { getAnalyticsSessionId, getDeviceType, newViewId } from "./session"

// Tracks active time-on-page with the use-page-view hook and upserts it to the
// backend (keyed by a per-visit viewId so heartbeats don't duplicate rows).
//
// Always on: this is cookie-free and collects no PII (per-tab sessionStorage id
// and coarse device type only), so it runs regardless of cookie consent.
//
// Mounted with `key={pathname}` by the parent so every client-side navigation
// starts a fresh visit with a new viewId and timer.
function Tracker({ pathname }: { pathname: string }) {
  const viewIdRef = useRef<string>("")
  if (!viewIdRef.current) {
    viewIdRef.current = newViewId()
  }

  usePageView({
    pageId: pathname,
    minTimeThreshold: 2,
    heartbeatInterval: 15,
    onPageView: async (data) => {
      try {
        await fetch(PA_PAGE_VIEWS_ENDPOINT, {
          method: "POST",
          headers: { "content-type": "application/json", ...PA_HEADERS },
          keepalive: true,
          body: JSON.stringify({
            viewId: viewIdRef.current,
            pageId: data.pageId,
            userId: data.userId ?? null,
            sessionId: getAnalyticsSessionId() ?? null,
            timeSpent: Math.round(data.timeSpent),
            isActive: data.isActive,
            deviceType: getDeviceType(),
          }),
        })
      } catch {
        // best-effort; never let analytics break navigation
      }
    },
  })

  return null
}

export default function PageViewTracker() {
  const pathname = usePathname()
  return <Tracker key={pathname} pathname={pathname} />
}
